import { FormFieldConfig } from '@shared/components/search-form/search-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';

// ============================================================================
// INTERFACES
// ============================================================================

export interface VendorItem {
  id: string;
  code: string;
  name: string;
  phone: string;
  email: string;
  address: string;
  vendorType: string;
  category: string;
  status: string;
  creationDate: string;
  // Additional fields for edit form
  description?: string;
  country?: string;
  taxNumber?: string;
  billingCurrency?: string;
  paymentTerm?: string;
  paymentMethod?: string;
  bankAccountName?: string;
  bankAccountNumber?: string;
  bankName?: string;
  swiftCode?: string;
  riskLevel?: string;
  vendorPostingGroup?: string;
  generalBusinessPostingGroup?: string;
  vatBusinessPostingGroup?: string;
  invoiceDiscCode?: string;
}

export interface VendorListResponse {
  data: VendorItem[];
  totalRecords: number;
  loading: boolean;
}

// ============================================================================
// STATUS OPTIONS
// ============================================================================

export const VENDOR_STATUS_OPTIONS = [
  { label: 'vendor.status.draft', value: 'DRAFT' },
  { label: 'vendor.status.reviewing', value: 'REVIEWING' },
  { label: 'vendor.status.rejected', value: 'REJECTED' },
  { label: 'vendor.status.approved', value: 'APPROVED' },
  { label: 'vendor.status.active', value: 'ACTIVE' },
  { label: 'vendor.status.inactive', value: 'INACTIVE' },
];

// Status options used by list filter
export const VENDOR_STATUS_FILTER_OPTIONS = [
  { label: 'vendor.status.draft', value: 'DRAFT' },
  { label: 'vendor.status.reviewing', value: 'REVIEWING' },
  { label: 'vendor.status.rejected', value: 'REJECTED' },
  { label: 'vendor.status.approved', value: 'APPROVED' },
  { label: 'vendor.status.active', value: 'ACTIVE' },
  { label: 'vendor.status.inactive', value: 'INACTIVE' },
];

// ============================================================================
// SEARCH FORM CONFIGURATION
// ============================================================================

export const VENDOR_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'code',
    label: 'vendor.search.fields.code',
    type: 'text',
    placeholder: 'vendor.search.placeholders.enter_code',
  },
  {
    key: 'name',
    label: 'vendor.search.fields.name',
    type: 'text',
    placeholder: 'vendor.search.placeholders.enter_name',
  },
  {
    key: 'phone',
    label: 'vendor.search.fields.phone',
    type: 'text',
    placeholder: 'vendor.search.placeholders.enter_phone',
  },
  {
    key: 'status',
    label: 'vendor.table.columns.status',
    type: 'select',
    options: VENDOR_STATUS_FILTER_OPTIONS,
    showClear: true,
  },
];

// ============================================================================
// TABLE COLUMN CONFIGURATION
// ============================================================================

/**
 * Default column property names (shown on first load)
 * Update these constants to change column property names
 */
export const VENDOR_DEFAULT_COLUMNS = {
  CODE: 'code',
  NAME: 'name',
  PHONE: 'phone',
  EMAIL: 'email',
  ADDRESS: 'address',
  VENDOR_TYPE: 'vendorType',
  CATEGORY: 'category',
  STATUS: 'status',
  CREATION_DATE: 'creationDate',
} as const;

/**
 * Flexible column property names (can be shown/hidden via column selector)
 * Update these constants to change flexible column property names
 */
export const VENDOR_FLEXIBLE_COLUMNS = {
  DESCRIPTION: 'description',
  COUNTRY: 'country',
  VENDOR_TYPE: 'vendorType',
  BILLING_CURRENCY: 'billingCurrency',
  PAYMENT_TERM: 'paymentTerm',
  PAYMENT_METHOD: 'paymentMethod',
  CATEGORY: 'category',
  RISK_LEVEL: 'riskLevel',
} as const;

/**
 * Action column property name
 */
export const VENDOR_ACTION_COLUMN = {
  ACTIONS: 'actions',
} as const;

/**
 * Default columns order (shown on first load)
 * Update this array to change the default column order
 */
export const VENDOR_DEFAULT_COLUMNS_ORDER: string[] = [
  VENDOR_DEFAULT_COLUMNS.CODE,
  VENDOR_DEFAULT_COLUMNS.NAME,
  VENDOR_DEFAULT_COLUMNS.PHONE,
  VENDOR_DEFAULT_COLUMNS.EMAIL,
  VENDOR_DEFAULT_COLUMNS.ADDRESS,
  VENDOR_DEFAULT_COLUMNS.VENDOR_TYPE,
  VENDOR_DEFAULT_COLUMNS.CATEGORY,
  VENDOR_DEFAULT_COLUMNS.STATUS,
  VENDOR_DEFAULT_COLUMNS.CREATION_DATE,
];

/**
 * Flexible columns array (can be shown/hidden via column selector)
 * Update this array to change which columns are flexible
 */
export const VENDOR_FLEXIBLE_COLUMNS_ORDER: string[] = [
  VENDOR_FLEXIBLE_COLUMNS.DESCRIPTION,
  VENDOR_FLEXIBLE_COLUMNS.COUNTRY,
  VENDOR_FLEXIBLE_COLUMNS.VENDOR_TYPE,
  VENDOR_FLEXIBLE_COLUMNS.BILLING_CURRENCY,
  VENDOR_FLEXIBLE_COLUMNS.PAYMENT_TERM,
  VENDOR_FLEXIBLE_COLUMNS.PAYMENT_METHOD,
  VENDOR_FLEXIBLE_COLUMNS.CATEGORY,
  VENDOR_FLEXIBLE_COLUMNS.RISK_LEVEL,
];

export const VENDOR_COLUMNS: TableColumn[] = [
  {
    label: 'vendor.table.columns.code',
    prop: VENDOR_DEFAULT_COLUMNS.CODE,
    width: 120,
    align: 'left',
  },
  {
    label: 'vendor.table.columns.name',
    prop: VENDOR_DEFAULT_COLUMNS.NAME,
    width: 150,
    align: 'left',
  },
  {
    label: 'vendor.table.columns.phone',
    prop: VENDOR_DEFAULT_COLUMNS.PHONE,
    width: 120,
    align: 'left',
  },
  {
    label: 'vendor.table.columns.email',
    prop: VENDOR_DEFAULT_COLUMNS.EMAIL,
    width: 150,
    align: 'left',
  },
  {
    label: 'vendor.table.columns.address',
    prop: VENDOR_DEFAULT_COLUMNS.ADDRESS,
    width: 200,
    align: 'left',
  },
  {
    label: 'vendor.table.columns.vendor_type',
    prop: VENDOR_DEFAULT_COLUMNS.VENDOR_TYPE,
    width: 150,
    type: 'custom',
    align: 'left',
  },
  {
    label: 'vendor.table.columns.category',
    prop: VENDOR_DEFAULT_COLUMNS.CATEGORY,
    width: 150,
    align: 'left',
  },
  {
    label: 'vendor.table.columns.status',
    prop: VENDOR_DEFAULT_COLUMNS.STATUS,
    width: 120,
    type: 'custom',
    align: 'center',
  },
  {
    label: 'vendor.table.columns.creation_date',
    prop: VENDOR_DEFAULT_COLUMNS.CREATION_DATE,
    width: 150,
    type: 'custom',
    align: 'left',
  },
  {
    label: 'vendor.table.columns.description',
    prop: VENDOR_FLEXIBLE_COLUMNS.DESCRIPTION,
    width: 200,
    align: 'left',
  },
  {
    label: 'vendor.table.columns.country',
    prop: VENDOR_FLEXIBLE_COLUMNS.COUNTRY,
    width: 120,
    align: 'left',
    type: 'custom',
  },
  {
    label: 'vendor.table.columns.billing_currency',
    prop: VENDOR_FLEXIBLE_COLUMNS.BILLING_CURRENCY,
    width: 150,
    align: 'left',
    type: 'custom',
  },
  {
    label: 'vendor.table.columns.payment_term',
    prop: VENDOR_FLEXIBLE_COLUMNS.PAYMENT_TERM,
    width: 200,
    align: 'left',
    type: 'custom',
  },
  {
    label: 'vendor.table.columns.payment_method',
    prop: VENDOR_FLEXIBLE_COLUMNS.PAYMENT_METHOD,
    width: 150,
    align: 'left',
    type: 'custom',
  },
  {
    label: 'vendor.table.columns.risk_level',
    prop: VENDOR_FLEXIBLE_COLUMNS.RISK_LEVEL,
    width: 120,
    align: 'left',
    type: 'custom',
  },
  {
    label: 'vendor.table.columns.actions',
    prop: VENDOR_ACTION_COLUMN.ACTIONS,
    type: 'actions',
    width: 200,
    align: 'center',
  },
];

// ============================================================================
// BUTTON CONFIGURATION
// ============================================================================

export const VENDOR_BUTTON_CONFIG = {
  label: 'common.actions.new',
  severity: 'info' as const,
  show: true,
  icon: 'add',
};

// ============================================================================
// STATUS MAPPING FUNCTIONS
// ============================================================================

export const getStatusSeverity = (
  status: string
): 'success' | 'warning' | 'danger' | 'secondary' => {
  switch (status) {
    case 'ACTIVE':
    case 'APPROVED':
      return 'success';
    case 'REVIEWING':
    case 'INACTIVE':
      return 'warning';
    case 'REJECTED':
      return 'danger';
    case 'DRAFT':
    default:
      return 'secondary';
  }
};

export const getStatusDisplayText = (
  status: string,
  translateService?: any
): string => {
  const statusMap: { [key: string]: string } = {
    DRAFT: 'vendor.status.draft',
    REVIEWING: 'vendor.status.reviewing',
    REJECTED: 'vendor.status.rejected',
    APPROVED: 'vendor.status.approved',
    ACTIVE: 'vendor.status.active',
    INACTIVE: 'vendor.status.inactive',
  };

  const translationKey = statusMap[status] || 'vendor.status.draft';
  return translateService
    ? translateService.instant(translationKey)
    : translationKey;
};

export const getStatusIcon = (status: string): string => {
  switch (status) {
    case 'ACTIVE':
    case 'APPROVED':
      return 'pi pi-check-circle text-green-500';
    case 'REVIEWING':
    case 'INACTIVE':
      return 'pi pi-clock text-yellow-500';
    case 'REJECTED':
      return 'pi pi-times-circle text-red-500';
    case 'DRAFT':
      return 'pi pi-file text-gray-500';
    default:
      return 'pi pi-file text-gray-500';
  }
};

export const getStatusStyle = (status: string): any => {
  const severity = getStatusSeverity(status);

  switch (severity) {
    case 'success':
      return {
        'background-color': '#ecfdf5',
        color: '#047857',
        border: 'none',
      };
    case 'warning':
      return {
        'background-color': '#fef3c7',
        color: '#a16207',
        border: 'none',
      };
    case 'danger':
      return {
        'background-color': '#fef2f2',
        color: '#dc2626',
        border: 'none',
      };
    case 'secondary':
    default:
      return {
        'background-color': '#f9fafb',
        color: '#6b7280',
        border: 'none',
      };
  }
};

// ============================================================================
// DISPLAY TEXT FUNCTIONS
// ============================================================================

/**
 * Get country display text by code
 * @param countryCode - Country code (e.g., 'VN')
 * @param countriesService - CountriesService instance
 * @param translateService - TranslateService instance
 * @returns Country display name
 */
export const getCountryDisplayText = (
  countryCode: string,
  countriesService?: any,
  translateService?: any
): string => {
  if (!countryCode) return '';

  if (countriesService) {
    const countries = countriesService.getCountries();
    const country = countries.find((c: any) => c.code === countryCode);
    if (country) {
      // Use name_en for English, name for Vietnamese
      if (translateService) {
        const currentLang = translateService.currentLang || 'en';
        return currentLang === 'vi' ? country.name : country.name_en;
      }
      return country.name_en || country.name || countryCode;
    }
  }

  return countryCode;
};

/**
 * Get payment term display text
 * @param paymentTerm - Payment term code (e.g., 'NET30', 'NET60', 'PREPAID', 'COD')
 * @param translateService - TranslateService instance
 * @returns Translated payment term text
 */
export const getPaymentTermDisplayText = (
  paymentTerm: string,
  translateService?: any
): string => {
  if (!paymentTerm) return '';

  const termMap: { [key: string]: string } = {
    NET30: 'vendor.form.payment_term.net30',
    NET60: 'vendor.form.payment_term.net60',
    PREPAID: 'vendor.form.payment_term.prepaid',
    COD: 'vendor.form.payment_term.cod',
  };

  const translationKey = termMap[paymentTerm] || paymentTerm;
  return translateService
    ? translateService.instant(translationKey)
    : translationKey;
};

/**
 * Get payment method display text
 * @param paymentMethod - Payment method code (e.g., 'BANK_TRANSFER', 'CASH', 'COD')
 * @param translateService - TranslateService instance
 * @returns Translated payment method text
 */
export const getPaymentMethodDisplayText = (
  paymentMethod: string,
  translateService?: any
): string => {
  if (!paymentMethod) return '';

  const methodMap: { [key: string]: string } = {
    BANK_TRANSFER: 'vendor.form.payment_method.bank_transfer',
    CASH: 'vendor.form.payment_method.cash',
    COD: 'vendor.form.payment_method.cod',
  };

  const translationKey = methodMap[paymentMethod] || paymentMethod;
  return translateService
    ? translateService.instant(translationKey)
    : translationKey;
};

/**
 * Get risk level display text
 * @param riskLevel - Risk level code (e.g., 'LOW', 'MEDIUM', 'HIGH')
 * @param translateService - TranslateService instance
 * @returns Translated risk level text
 */
export const getRiskLevelDisplayText = (
  riskLevel: string,
  translateService?: any
): string => {
  if (!riskLevel) return '';

  const levelMap: { [key: string]: string } = {
    LOW: 'vendor.form.risk_level.low',
    MEDIUM: 'vendor.form.risk_level.medium',
    HIGH: 'vendor.form.risk_level.high',
  };

  const translationKey = levelMap[riskLevel] || riskLevel;
  return translateService
    ? translateService.instant(translationKey)
    : translationKey;
};
